import React, { useMemo } from "react";
import type { NormalisedProduct, NormalisedVariant } from "@app/scripts/types";
import { CartProductDiscount } from "@app/scripts/react-cart/components/CartProductDiscount";
import { formatPrice } from "@app/scripts/utils";
import { useLocalisation } from "@app/scripts/react-hooks/localisation";

type Props = {
  product: NormalisedProduct;
  qty?: number;
  lineItem?: any;
  variant: "product-card" | null;
  selectedVariant?: NormalisedVariant | null;
  showCartDiscount?: boolean;
};

export const ProductPrice: React.FC<Props> = ({
  product,
  qty,
  lineItem,
  variant = null,
  selectedVariant = null,
  showCartDiscount = false,
}) => {
  const { localisation } = useLocalisation();

  const priceRange = useMemo(() => {
    const variants = product?.variants?.sort((a, b) => a.price - b.price);

    const minVariant = variants?.[0];
    const maxVariant = variants?.at(-1);

    const presentmentPriceMin = localisation?.currency_code
      ? minVariant?.presentment_prices?.find((price) => price.currency_code === localisation.currency_code)
      : null;

    const presentmentPriceMax = localisation?.currency_code
      ? maxVariant?.presentment_prices?.find((price) => price.currency_code === localisation.currency_code)
      : null;

    return {
      lowest: {
        price: presentmentPriceMin?.price || minVariant?.price,
        compare_at: presentmentPriceMin?.compare_at_price || minVariant?.compare_at_price,
      },
      highest: {
        price: presentmentPriceMax?.price || maxVariant?.price,
        compare_at: presentmentPriceMax?.compare_at_price || maxVariant?.compare_at_price,
      },
    };
  }, [product, localisation]);

  const price = useMemo(() => {
    const lowest = priceRange.lowest.price;
    const highest = priceRange.highest.price;
    if (lowest === highest) return lowest;
    if (selectedVariant) {
      return (
        selectedVariant?.presentment_prices?.find((price) => price.currency_code === localisation?.currency_code)
          ?.price || selectedVariant?.price
      );
    }
    return `${lowest} - $${highest}`;
  }, [priceRange, selectedVariant]);

  const compareAt = useMemo(() => {
    const lowest = priceRange.lowest.compare_at;
    const highest = priceRange.highest.compare_at;
    if (selectedVariant) return selectedVariant?.compare_at_price;
    if (lowest === highest) return lowest;
    return `${lowest} - $${highest}`;
  }, [priceRange, selectedVariant]);

  const onSale = useMemo(() => {
    if (variant !== "product-card") {
      return lineItem
        ? lineItem.original_line_price > lineItem.final_line_price
        : product?.compare_at_price && product?.price < product?.compare_at_price;
    }

    const { lowest, highest } = priceRange;

    return lowest?.price < lowest?.compare_at && highest?.price < highest?.compare_at;
  }, [product, variant]);

  const symbol = useMemo(() => localisation?.currency_symbol || "$", [localisation]);
  const currencyCode = useMemo(() => localisation?.currency_code || "", [localisation]);

  return (
    <div className="price">
      <div className="price__container">
        {!onSale ? (
          <div className="price__regular">
            <span className="visually-hidden visually-hidden--inline">Regular price</span>
            <span className="price-item price-item--regular">
              {variant === "product-card" ? (
                <>{symbol}{price}</>
              ) : (
                <>
                  {formatPrice(lineItem?.final_line_price ? lineItem?.final_line_price / 100 : product.price, currencyCode)}
                </>
              )}
            </span>
          </div>
        ) : (
          <div className="price__sale">
            <span className="price-item price-item--sale">
              {variant === "product-card" ? (
                <>{symbol}{price}</>
              ) : (
                <>
                  {formatPrice(lineItem?.final_line_price ? lineItem?.final_line_price / 100 : product.price, currencyCode)}
                </>
              )}
            </span>
            <span className="visually-hidden visually-hidden--inline">Regular price</span>
            <span>
              <s className="price-item price-item--regular">
                {variant === "product-card" ? (
                  <>{symbol}{compareAt}</>
                ) : (
                  <>
                    {formatPrice(lineItem
                      ? lineItem.original_line_price / 100
                      : qty && product.compare_at_price
                        ? product.compare_at_price * qty
                        : product.compare_at_price || 0,
                      currencyCode)}
                  </>
                )}
              </s>
            </span>
            <span className="visually-hidden visually-hidden--inline">Sale price</span>
          </div>
        )}
      </div>
      {showCartDiscount && lineItem && <CartProductDiscount lineItem={lineItem} />}
    </div>
  );
};
